<template>
  <div class="login-screen">
    <!-- Modo login ------------------------------------------------------- -->
    <div v-if="mode === 'login'" class="login-card">
      <div class="login-type">
        <label class="login-type-label" for="login-type">TIPO DE CUENTA</label>
        <div class="login-select-wrap">
          <select
            id="login-type"
            class="login-select"
            :value="loginType"
            @change="selectType($event.target.value)"
          >
            <option v-for="tab in tabs" :key="tab.key" :value="tab.key">
              {{ tab.label }}
            </option>
          </select>
          <span class="login-select-caret" aria-hidden="true">▾</span>
        </div>
      </div>

      <h1 class="login-title">{{ activeTab.title }}</h1>
      <p class="login-sub">{{ activeTab.sub }}</p>

      <div v-if="errorMessage" class="login-error">{{ errorMessage }}</div>

      <!-- Cuenta creada pero sin confirmar: se ofrece reenviar el enlace. -->
      <template v-if="verificationRequired">
        <div v-if="resentMessage" class="login-success">{{ resentMessage }}</div>
        <button
          v-else
          type="button"
          class="login-btn"
          :disabled="resending"
          @click="handleResendVerification"
        >
          {{ resending ? 'ENVIANDO…' : 'REENVIAR CORREO DE CONFIRMACIÓN' }}
        </button>
      </template>

      <form class="login-form" @submit.prevent="handleSubmit">
        <input
          v-model="username"
          class="login-input"
          type="text"
          name="usuario"
          placeholder="Email"
          autocomplete="username"
        />
        <input
          v-model="password"
          class="login-input"
          type="password"
          name="password"
          placeholder="Contraseña"
          autocomplete="current-password"
        />
        <button type="submit" class="login-btn" :disabled="loading">
          {{ loading ? 'INGRESANDO…' : 'INGRESAR' }}
        </button>
      </form>

      <a href="#" class="login-forgot" @click.prevent="openForgot">
        ¿Olvidaste tu contraseña?
      </a>

      <!-- Solo los clientes pueden crear una cuenta; las cuentas de compañía
           y de administrador las crea el administrador. -->
      <template v-if="loginType === 'client'">
        <div class="login-divider"></div>
        <p class="login-register-text">¿NO TE HAS REGISTRADO?</p>
        <a href="/registro/" class="login-register">REGISTRARSE</a>
      </template>
    </div>

    <!-- Modo recuperar contraseña --------------------------------------- -->
    <div v-else class="login-card">
      <h1 class="login-title">RECUPERAR CONTRASEÑA</h1>
      <p class="login-sub">
        Ingresa tu correo y te enviaremos un enlace para crear una nueva contraseña.
      </p>

      <div v-if="errorMessage" class="login-error">{{ errorMessage }}</div>
      <div v-if="forgotSent" class="login-success">{{ forgotMessage }}</div>

      <form v-if="!forgotSent" class="login-form" @submit.prevent="handleForgotSubmit">
        <input
          v-model="username"
          class="login-input"
          type="email"
          placeholder="Email"
          autocomplete="username"
        />
        <button type="submit" class="login-btn" :disabled="loading">
          {{ loading ? 'ENVIANDO…' : 'ENVIAR ENLACE' }}
        </button>
      </form>

      <a href="#" class="login-forgot" @click.prevent="backToLogin">
        ← Volver a iniciar sesión
      </a>
    </div>
  </div>
</template>

<script>
import { requestPasswordReset, resendVerificationEmail } from "@/services/teatroApi";

// El orden es el del selector: primero el acceso por defecto (cliente).
const LOGIN_TABS = [
  {
    key: "client",
    label: "CLIENTE",
    title: "SESIÓN CLIENTES",
    sub: "Ingresa con tu correo electrónico y contraseña",
  },
  {
    key: "company",
    label: "COMPAÑÍA DE TEATRO",
    title: "SESIÓN COMPAÑÍA",
    sub: "Ingresa con el correo y contraseña de tu compañía",
  },
  {
    key: "admin",
    label: "ADMINISTRADOR",
    title: "SESIÓN ADMINISTRADOR",
    sub: "Ingresa con tu correo y contraseña de administrador",
  },
];

export default {
  name: "LoginTeatro",
  data() {
    return {
      username: "",
      password: "",
      loading: false,
      errorMessage: "",
      tabs: LOGIN_TABS,
      loginType: "client",
      mode: "login", // 'login' | 'forgot'
      forgotSent: false,
      forgotMessage: "",
      // El login devuelve `verification_required` cuando la contraseña es
      // correcta pero la cuenta todavía no confirmó su correo.
      verificationRequired: false,
      pendingEmail: "",
      resending: false,
      resentMessage: "",
    };
  },
  computed: {
    activeTab() {
      return this.tabs.find((t) => t.key === this.loginType) || this.tabs[0];
    },
  },
  mounted() {
    this.initTypeFromUrl();
    this.checkForErrorMessages();
  },
  methods: {
    initTypeFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      const tipo = (urlParams.get("tipo") || "").toLowerCase();
      if (this.tabs.some((t) => t.key === tipo)) {
        this.loginType = tipo;
      }
    },
    selectType(key) {
      this.loginType = key;
      this.errorMessage = "";
      // Reflejar el portal elegido en la URL sin recargar.
      const url = new URL(window.location.href);
      url.searchParams.set("tipo", key);
      window.history.replaceState({}, "", url);
    },
    checkForErrorMessages() {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("error") === "1") {
        this.errorMessage = "Usuario o contraseña incorrectos";
      }
      const storedError = sessionStorage.getItem("loginError");
      if (storedError) {
        this.errorMessage = storedError;
        sessionStorage.removeItem("loginError");
      }
    },
    openForgot() {
      this.mode = "forgot";
      this.errorMessage = "";
      this.forgotSent = false;
      this.forgotMessage = "";
    },
    backToLogin() {
      this.mode = "login";
      this.errorMessage = "";
    },
    async handleForgotSubmit() {
      const email = (this.username || "").trim();
      if (!email) {
        this.errorMessage = "Ingresa tu correo electrónico.";
        return;
      }
      this.loading = true;
      this.errorMessage = "";
      try {
        const data = await requestPasswordReset(email);
        this.forgotSent = true;
        this.forgotMessage =
          data.message ||
          "Si el correo está registrado, te enviamos un enlace para restablecer tu contraseña.";
      } catch (error) {
        this.errorMessage =
          error.response?.data?.error ||
          "No pudimos enviar el correo. Inténtalo más tarde.";
      } finally {
        this.loading = false;
      }
    },
    async handleResendVerification() {
      this.resending = true;
      try {
        const data = await resendVerificationEmail(this.pendingEmail);
        this.resentMessage =
          data.message || "Te enviamos un correo nuevo con el enlace.";
      } catch (error) {
        this.errorMessage =
          error.response?.data?.error || "No pudimos enviar el correo.";
      } finally {
        this.resending = false;
      }
    },
    async handleSubmit() {
      if (!this.username || !this.password) {
        this.errorMessage = "Ingresa tu usuario y contraseña.";
        return;
      }

      this.loading = true;
      this.errorMessage = "";
      this.verificationRequired = false;
      this.resentMessage = "";

      try {
        const formData = new FormData();
        formData.append("usuario", this.username);
        formData.append("password", this.password);
        formData.append("tipo", this.loginType);

        const response = await fetch("/login_user/", {
          method: "POST",
          body: formData,
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            window.location.href = data.redirect_url;
          } else {
            this.errorMessage = data.error || "Usuario o contraseña incorrectos";
          }
        } else if (response.redirected) {
          window.location.href = response.url;
        } else {
          const data = await response.json().catch(() => ({}));
          this.errorMessage = data.error || "Usuario o contraseña incorrectos";
          if (data.verification_required) {
            this.verificationRequired = true;
            this.pendingEmail = data.email || this.username;
          }
        }
      } catch (error) {
        console.error("Error en el login:", error);
        this.errorMessage = "Ocurrió un error al intentar iniciar sesión";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.login-screen {
  min-height: calc(100vh - 40px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
}

.login-card {
  width: 100%;
  max-width: 420px;
  background: rgba(10, 6, 6, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 22px;
  padding: 40px 38px 34px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);
  text-align: center;
  backdrop-filter: blur(2px);
}

.login-type {
  text-align: left;
  margin-bottom: 24px;
}

.login-type-label {
  display: block;
  color: rgba(255, 255, 255, 0.6);
  font-family: var(--font-family-heading);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.2px;
  margin-bottom: 7px;
}

.login-select-wrap {
  position: relative;
}

.login-select {
  width: 100%;
  appearance: none;
  -webkit-appearance: none;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  padding: 13px 38px 13px 16px;
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.login-select:hover,
.login-select:focus {
  background: rgba(255, 255, 255, 0.11);
  border-color: rgba(200, 16, 46, 0.75);
}

/* El desplegable lo dibuja el sistema con fondo claro: las opciones van
   en oscuro para que se lean. */
.login-select option {
  color: #1a1a1a;
  background: #fff;
}

.login-select-caret {
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  pointer-events: none;
}

.login-title {
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 2px;
  margin: 0 0 8px;
}

.login-sub {
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  letter-spacing: 0.4px;
  margin: 0 0 26px;
}

.login-error {
  background: rgba(200, 16, 46, 0.15);
  border: 1px solid rgba(200, 16, 46, 0.5);
  color: #ffb3b3;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 12px;
  margin-bottom: 16px;
}

.login-success {
  background: rgba(46, 160, 87, 0.15);
  border: 1px solid rgba(46, 160, 87, 0.5);
  color: #a7e8bf;
  border-radius: 8px;
  padding: 11px 14px;
  font-size: 12.5px;
  line-height: 1.5;
  margin-bottom: 16px;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.login-input {
  width: 100%;
  background: #fff;
  border: none;
  border-radius: 8px;
  padding: 13px 16px;
  font-family: var(--font-family-primary);
  font-size: 13px;
  color: #1a1a1a;
  outline: none;
}

.login-input::placeholder {
  color: #9a9a9a;
}

.login-btn {
  margin-top: 6px;
  width: 100%;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 13px;
  font-family: var(--font-family-heading);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(200, 16, 46, 0.4);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(200, 16, 46, 0.5);
}

.login-btn:disabled {
  opacity: 0.6;
  cursor: default;
  transform: none;
  box-shadow: none;
}

.login-forgot {
  display: inline-block;
  margin-top: 18px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 11px;
  text-decoration: none;
  transition: color 0.2s ease;
}

.login-forgot:hover {
  color: #fff;
}

.login-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.15);
  margin: 22px 0 18px;
}

.login-register-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  letter-spacing: 0.5px;
  margin: 0 0 6px;
}

.login-register {
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 1px;
  text-decoration: none;
  transition: color 0.2s ease;
}

.login-register:hover {
  color: #ff6b6b;
}
</style>
